/**
 * Generate JSON Schema for Google Shopping Product Feed
 * 符合 Google Merchant Center Product Feed Specification
 * @see https://support.google.com/merchants/answer/7052112
 * @param {Array} parsedData - Parsed CSV data
 * @param {'shopline' | 'shopify'} platform - Platform type
 * @returns {string} - JSON Schema string
 */
export function generateJsonSchema(parsedData, platform = 'shopline') {
  const schema = createGoogleShoppingSchema(platform);

  if (parsedData.length > 0) {
    // 从实际数据中推断可选字段是否存在
    inferOptionalFields(schema, parsedData);
  }

  return JSON.stringify(schema, null, 2);
}

/**
 * Create Google Shopping Product Feed JSON Schema
 * @param {'shopline' | 'shopify'} platform - Platform type
 * @returns {Object} - Google Shopping schema object
 */
function createGoogleShoppingSchema(platform = 'shopline') {
  const platformName = platform === 'shopify' ? 'Shopify' : 'SHOPLINE';
  return {
    "$id": `https://example.com/schemas/google-shopping-product-feed`,
    "$schema": "https://json-schema.org/draft/2020-12/schema",
    "title": "Google Shopping Product Feed",
    "description": `Schema for ${platformName} product data exported for Google Merchant Center. Follows Google Shopping Product Feed Specification.`,
    "type": "object",
    "properties": {
      // ========== OpenAI Flags ==========
      "enable_search": {
        "type": "string",
        "enum": ["true", "false"],
        "description": "Enable search in AI-powered commerce (OpenAI specific)"
      },
      "enable_checkout": {
        "type": "string",
        "enum": ["true", "false"],
        "description": "Enable checkout in AI-powered commerce (OpenAI specific)"
      },

      // ========== Basic Product Data (Google Shopping Required) ==========
      "id": {
        "type": "string",
        "description": "Unique product identifier (Required by Google Shopping)"
      },
      "title": {
        "type": "string",
        "description": "Product title (Required by Google Shopping)"
      },
      "description": {
        "type": "string",
        "description": "Product description (Required by Google Shopping)"
      },
      "link": {
        "type": "string",
        "format": "uri",
        "description": "Product landing page URL (Required by Google Shopping)"
      },

      // ========== Product Identifiers (Google Shopping Strongly Recommended) ==========
      "gtin": {
        "type": ["string", "null"],
        "description": "Global Trade Item Number (GTIN, EAN, UPC, JAN, ISBN)"
      },
      "mpn": {
        "type": ["string", "null"],
        "description": "Manufacturer Part Number"
      },
      "sku": {
        "type": ["string", "null"],
        "description": "Stock Keeping Unit"
      },

      // ========== Item Information ==========
      "condition": {
        "type": "string",
        "enum": ["new", "refurbished", "used"],
        "description": "Item condition (Required by Google Shopping)"
      },
      "product_category": {
        "type": "string",
        "description": "Product category or type (Google's product_type)"
      },
      "brand": {
        "type": "string",
        "description": "Brand name (Strongly recommended by Google Shopping)"
      },
      "material": {
        "type": "string",
        "description": "Product material"
      },
      "weight": {
        "type": ["string", "null"],
        "description": "Product weight with unit (e.g., '1.5 kg')"
      },

      // ========== Media ==========
      "image_link": {
        "type": ["string", "null"],
        "format": "uri",
        "description": "Main product image URL (Required by Google Shopping)"
      },
      "additional_image_link": {
        "type": "array",
        "items": {
          "type": "string",
          "format": "uri"
        },
        "description": "Additional product image URLs"
      },

      // ========== Price & Promotions ==========
      "price": {
        "type": "string",
        "pattern": "^[0-9]+(\\.[0-9]{1,4})? [A-Z]{3}$",
        "description": "Price with currency code (Required by Google Shopping, e.g., '9.99 USD')",
        "examples": ["9.99 USD", "100.00 EUR"]
      },
      "sale_price": {
        "type": ["string", "null"],
        "pattern": "^[0-9]+(\\.[0-9]{1,4})? [A-Z]{3}$",
        "description": "Sale price with currency code (e.g., '7.99 USD')"
      },

      // ========== Availability & Inventory ==========
      "availability": {
        "type": "string",
        "enum": ["in_stock", "out_of_stock", "preorder", "backorder"],
        "description": "Availability status (Required by Google Shopping)"
      },
      "inventory_quantity": {
        "type": "integer",
        "minimum": 0,
        "description": "Inventory quantity"
      },

      // ========== Variants ==========
      "item_group_id": {
        "type": ["string", "null"],
        "description": "Product group ID for variants (Required for variant products)"
      },
      "item_group_title": {
        "type": ["string", "null"],
        "description": "Product group title for variants"
      },
      "color": {
        "type": ["string", "null"],
        "description": "Product color variant"
      },
      "size": {
        "type": ["string", "null"],
        "description": "Product size variant"
      },
      "offer_id": {
        "type": ["string", "null"],
        "description": "Unique offer identifier"
      },

      // ========== Merchant Info ==========
      "seller_name": {
        "type": "string",
        "description": "Seller or merchant name"
      },
      "seller_url": {
        "type": "string",
        "format": "uri",
        "description": "Seller website URL"
      },

      // ========== Semantic Data (SkuSync specific) ==========
      "triples": {
        "type": "array",
        "items": {
          "type": "array"
        },
        "description": "Semantic triples for AI understanding (SkuSync specific)"
      },

      // ========== Backward Compatibility ==========
      "stock": {
        "type": "integer",
        "description": "Stock quantity (legacy field)"
      },
      "url": {
        "type": "string",
        "format": "uri",
        "description": "Product URL (legacy field, use 'link' instead)"
      },
      "platform": {
        "type": "string",
        "description": "Source platform (shopline/shopify)"
      },
      "updated_at": {
        "type": "string",
        "description": "Last update timestamp"
      },

      // ========== Raw Data ==========
      "raw": {
        "type": "object",
        "description": "Original CSV row data (for reference)"
      }
    },
    "required": [
      "id",
      "title",
      "description",
      "link",
      "image_link",
      "availability",
      "price",
      "condition",
      "brand"
    ]
  };
}

/**
 * 从实际数据中推断可选字段是否存在
 * @param {Object} schema - Schema object to modify
 * @param {Array} parsedData - Parsed CSV data
 */
function inferOptionalFields(schema, parsedData) {
  const sample = parsedData[0];

  // 检查哪些可选字段在实际数据中存在且有值
  const optionalFields = [
    'gtin', 'mpn', 'sku', 'material', 'weight',
    'sale_price', 'item_group_id', 'color', 'size'
  ];

  optionalFields.forEach(field => {
    const hasField = parsedData.some(row =>
      row[field] !== null && row[field] !== undefined && row[field] !== ''
    );

    if (!hasField && schema.properties[field]) {
      // 标记为可能不存在
      schema.properties[field].description += ' (Not found in current data)';
    }
  });
}
